/**
 * The ONE frontend implementation of "upstream USD cost -> tokens we bill".
 *
 * This is a port of `cost_usd_to_billed_tokens()` in `constants/pricing/base.py`,
 * and it exists because the quote a user reads before pressing Generate must be
 * the number the server then charges. Until 2026-09 it was not: every price in
 * `videoSources.ts`, `klingVideo.ts`, `viduVideo.ts` and `seedance2Video.ts` was
 * a hand-rolled `ceil(cost * 1.5 * 1000)` frozen at a markup the backend had
 * already moved off, so the scene picker under-quoted every cloud video source
 * by 25-50% and the token balance check passed on a price that did not exist.
 *
 * WHY A FLOOR, not just a markup
 * ------------------------------
 * Tokens are not sold at their list value of $0.001. `constants/pricing/packages.py`
 * discounts them by volume down to $0.0007 (Enterprise). A job priced at a 1.5x
 * markup therefore collects 1.5 * 0.70 = 1.05x its own upstream cost, and at 1.1x
 * it is sold at a loss. `MIN_EFFECTIVE_MARKUP_MULTIPLIER` (~1.7857) is the markup
 * a domain has to reach before its own number matters at all, and
 * `costUsdToBilledTokens` takes the larger of the two -- exactly as the server does.
 *
 * EVERY NUMBER HERE IS READ, NOT COPIED
 * -------------------------------------
 * `shared_constants.json` is regenerated from the Python constants by
 * `npm run generate:all`, which `npm run type-check`, `npm run dev` and
 * `npm run build` all run first. So these four values cannot drift from the
 * backend: they are the backend's, re-read on every build. Do not replace an
 * import here with a literal (RULE #1.5, RULE #-2).
 *
 * What still has to be mirrored by hand -- the vendor cost rows for Veo, and the
 * per-provider run parameters -- is pinned instead by
 * `tests/test_frontend/test_frontend_video_prices_match_backend_regression.py`,
 * which executes the real backend estimators and fails on any disagreement.
 */

import sharedConstants from './shared_constants.json';

/** 1 token = $0.001 at list. Source: constants/pricing/base.py TOKENS_PER_USD. */
export const TOKENS_PER_USD: number = sharedConstants.constants.misc.TOKENS_PER_USD;

/**
 * The margin floor, expressed as the markup below which a domain's own markup is
 * inert. Source: constants/pricing/base.py MIN_EFFECTIVE_MARKUP_MULTIPLIER.
 */
export const MIN_EFFECTIVE_MARKUP_MULTIPLIER: number =
  sharedConstants.constants.limits.MIN_EFFECTIVE_MARKUP_MULTIPLIER;

/** Source: constants/pricing/base.py MINIMUM_TOKENS_PER_OPERATION. */
export const MINIMUM_TOKENS_PER_OPERATION: number =
  sharedConstants.constants.limits.MINIMUM_TOKENS_PER_OPERATION;

/** Source: constants/pricing/video_generation.py VIDEO_GENERATION_MARKUP_MULTIPLIER. */
export const VIDEO_GENERATION_MARKUP_MULTIPLIER: number =
  sharedConstants.constants.video.VIDEO_GENERATION_MARKUP_MULTIPLIER;

/**
 * Decimal places of a token the ceil below may see.
 *
 * Mirrors `_TOKEN_ROUNDING_PLACES` in constants/pricing/base.py. `cost * markup *
 * 1000` is binary floating point and a product that is mathematically an exact
 * integer can land one ULP above it, which `Math.ceil` then bills as a whole
 * extra token. Rounding at 1e-9 of a token discards at most 1e-12 USD -- nothing
 * we bill is that small, the minimum charge being one whole token.
 */
const TOKEN_ROUNDING_PLACES = 9;

/**
 * Tokens to bill for one unit of work costing `costUsd` upstream.
 *
 * Mirrors `constants/pricing/base.py::cost_usd_to_billed_tokens`. Never write the
 * multiplication out at a call site: `ceil(cost * markup * 1000)` skips the floor
 * and quotes a price the server will not charge.
 *
 * @param costUsd - Upstream (vendor / GPU) cost in USD of ONE unit of work.
 * @param markupMultiplier - The domain's markup over cost. Values below
 *   MIN_EFFECTIVE_MARKUP_MULTIPLIER are legal but inert: the floor overrides them.
 * @returns Tokens to bill, always at least MINIMUM_TOKENS_PER_OPERATION.
 * @throws RangeError - If the inputs cannot describe a price. Nothing is clamped
 *   and no number is invented: a bad input is a bug at the call site, and a
 *   made-up price would be shown to a real customer (RULE #5).
 */
export function costUsdToBilledTokens(costUsd: number, markupMultiplier: number): number {
  if (!Number.isFinite(costUsd) || costUsd < 0) {
    throw new RangeError(`costUsd must be a finite, non-negative number, got ${costUsd}`);
  }
  if (!Number.isFinite(markupMultiplier) || markupMultiplier < 1.0) {
    throw new RangeError(
      `markupMultiplier must be a finite number >= 1.0, got ${markupMultiplier}; ` +
        'a multiplier below 1.0 prices the job under its own upstream cost'
    );
  }
  const effectiveMultiplier = Math.max(markupMultiplier, MIN_EFFECTIVE_MARKUP_MULTIPLIER);
  const rawTokens = Number(
    (costUsd * effectiveMultiplier * TOKENS_PER_USD).toFixed(TOKEN_ROUNDING_PLACES)
  );
  return Math.max(MINIMUM_TOKENS_PER_OPERATION, Math.ceil(rawTokens));
}

/**
 * `costUsdToBilledTokens` at the video-generation markup.
 *
 * The markup every cloud video source is priced at server-side
 * (`VideoGenerationPricing.markup_multiplier`), so a caller cannot pick the wrong
 * one for a video quote.
 */
export function videoCostUsdToTokens(costUsd: number): number {
  return costUsdToBilledTokens(costUsd, VIDEO_GENERATION_MARKUP_MULTIPLIER);
}

/**
 * Read a vendor rate out of a price table, or refuse.
 *
 * The one place the frontend is allowed to handle "this key has no price". It
 * exists because the alternative kept getting written by hand as
 * `TABLE[key] ?? TABLE.someDefault`, and a default rate is a made-up price: the
 * seedance2 picker fell back from the $0.3034/s standard rate to the $0.2419/s
 * fast one (a 20% under-quote) and the WAN 2.6 picker from $0.15/s (1080p) to
 * $0.10/s (720p, a 33% under-quote) for any value the table did not carry --
 * silently, and only for the values a stale persisted config can hold, which is
 * exactly where nobody looks. The server has no such fallback; it raises.
 *
 * @param table - Price table keyed by a closed union.
 * @param key - The key to read. Typed as the union, but reachable at runtime
 *   from persisted state written by an older build.
 * @param what - What is being priced, for the error message.
 * @returns The rate recorded for *key*.
 * @throws RangeError - If *key* has no recorded rate (RULE #5: never invent one).
 */
export function requireRate<K extends string>(
  table: Record<K, number>,
  key: K,
  what: string
): number {
  const rate = table[key];
  if (typeof rate !== 'number' || !Number.isFinite(rate)) {
    throw new RangeError(
      `No ${what} rate recorded for '${key}'. Known: ${Object.keys(table).join(', ')}. ` +
        'Add the row rather than falling back to another rate -- a fallback quotes ' +
        'a price the server will not charge.'
    );
  }
  return rate;
}

/** Tokens back to the USD they are worth at list. Mirrors `tokens_to_usd`. */
export function tokensToUsd(tokens: number): number {
  return tokens / TOKENS_PER_USD;
}
